import React, { useEffect } from 'react';
import { toast } from 'react-toastify';
import { Button, IconButton, TextField, Tooltip } from '@material-ui/core';
import Paper from '@material-ui/core/Paper';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import CheckIcon from '@material-ui/icons/Check';
import ClearIcon from '@material-ui/icons/Clear';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import { useStylesForSettings } from '../../../components/SettingsComponents/settingsHelpers';
import SettingsTableHeader from '../../../components/SettingsComponents/SettingsTableHeader';
import { useHardwareMetaData } from '../../../hooks/useHardwareMetaData';
import { AddTypeFormValue } from './addType';
import { emptyType, UpdateType } from './updateType';
import { addTypeDtoInitialValues, useAddTypeData } from './useAddTypeData';
import { useDeleteTypeData } from './useDeleteTypeData';
import { useUpdateTypeData } from './useUpdateTypeData';

const TypesList = () => {
  const classes = useStylesForSettings();
  const data = useHardwareMetaData();
  const { submitDeleteType } = useDeleteTypeData();
  const { submitAddType } = useAddTypeData();
  const { submitUpdateType } = useUpdateTypeData();
  const [types, setTypes] = React.useState(data.types);
  const [selected, setSelected] = React.useState(-1);
  const [values, setValues] = React.useState<AddTypeFormValue>(addTypeDtoInitialValues);
  const [updatedValues, setUpdatedValues] = React.useState<UpdateType>(emptyType);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { target } = event;
    const { value } = target;
    const { name } = target;
    setValues({ ...values, [name]: value });
  };

  const handleSubmit = async (event: React.FormEvent<HTMLButtonElement>) => {
    try {
      const res = await submitAddType(values);
      if (res) {
        setTypes([...types, res as { id: number; name: string }]);
        setValues(addTypeDtoInitialValues);
      }
    } catch {
      toast.error('Error adding type');
    }
    event.preventDefault();
  };

  const handleUpdate = async (id: number) => {
    try {
      const resType = await submitUpdateType(id, updatedValues);
      if (resType === 'success') {
        setTypes(
          types.map((b) => (b.id === id ? { ...b, name: updatedValues.name } : b)),
        );
      }
      deactivateEditMode();
    } catch {
      toast.error('Error updating type');
    }
  };

  const handleDelete = async (id: number) => {
    try {
      const resType = await submitDeleteType(id);
      if (resType === 'success') {
        setTypes(types.filter((b) => b.id !== id));
      }
    } catch {
      toast.error('Error deleting type');
    }
  };

  const handleUpdateChange =
    (name: keyof UpdateType) => (event: React.ChangeEvent<HTMLInputElement>) => {
      setUpdatedValues({ ...updatedValues, [name]: event.target.value });
    };

  const handleClick = (id: number) => {
    setSelected(id);
  };

  function deactivateEditMode() {
    setSelected(-1);
  }

  useEffect(() => {
    setTypes(data.types);
  }, [data.types]);

  const isSelected = (id: number) => selected === id;

  return (
    <Paper className={classes.paper}>
      <div className={classes.tableWrapper}>
        <Table
          className={classes.table}
          aria-labelledby="tableTitle"
          size="small"
          aria-label="enhanced table"
        >
          <SettingsTableHeader />
          {types.length === 0 ? (
            <TableRow>
              <TableCell align="center">
                <TextField
                  id="standard-basic"
                  name="name"
                  value={values.name}
                  onChange={handleChange}
                  style={{ width: '20vw' }}
                  multiline
                />
              </TableCell>
              <TableCell align="center">
                <Button
                  className={classes.button}
                  variant="contained"
                  color="primary"
                  size="small"
                  onClick={handleSubmit}
                >
                  Add New
                </Button>
              </TableCell>
            </TableRow>
          ) : (
            <TableBody>
              <TableRow>
                <TableCell align="center">
                  <TextField
                    id="standard-basic"
                    name="name"
                    value={values.name}
                    onChange={handleChange}
                    style={{ width: '20vw' }}
                    multiline
                  />
                </TableCell>
                <TableCell align="center">
                  <Button
                    className={classes.button}
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={handleSubmit}
                  >
                    Add New
                  </Button>
                </TableCell>
              </TableRow>
              {types.map((d) => {
                const isItemSelected = isSelected(d.id);

                return (
                  <TableRow
                    role="checkbox"
                    aria-checked={isItemSelected}
                    tabIndex={-1}
                    key={d.id}
                  >
                    {isItemSelected ? (
                      <TableCell align="center">
                        <TextField
                          id="standard-basic"
                          name="name"
                          defaultValue={d.name}
                          style={{ width: '20vw' }}
                          onChange={handleUpdateChange('name')}
                          multiline
                        />
                      </TableCell>
                    ) : (
                      <TableCell align="center">{d.name}</TableCell>
                    )}
                    {isItemSelected ? (
                      <TableCell align="right">
                        <Tooltip title="save changes">
                          <IconButton
                            onClick={() => handleUpdate(d.id)}
                            className={classes.showDetailsIconButton}
                          >
                            <CheckIcon
                              fontSize="small"
                              className={classes.showDetailsIcon}
                            />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="cancel">
                          <IconButton
                            onClick={() => deactivateEditMode()}
                            className={classes.showDetailsIconButton}
                          >
                            <ClearIcon
                              fontSize="small"
                              className={classes.showDetailsIcon}
                            />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    ) : (
                      <TableCell align="right">
                        <Tooltip title="edit">
                          <IconButton
                            onClick={(event) => handleClick(d.id)}
                            className={classes.showDetailsIconButton}
                            disabled={selected !== -1}
                          >
                            <EditIcon
                              fontSize="small"
                              className={classes.showDetailsIcon}
                            />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="delete">
                          <IconButton
                            className={classes.showDetailsIconButton}
                            onClick={() => handleDelete(d.id)}
                          >
                            <DeleteIcon
                              fontSize="small"
                              className={classes.showDetailsIcon}
                            />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          )}
        </Table>
      </div>
    </Paper>
  );
};

export default TypesList;
